import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import Footer from "./Footer";
import "./Wishlist.css";
import {BASE_URL} from "./Constants";

const Wishlist = () => {
  const [wishlistItems, setWishlistItems] = useState([]);
  const [user, setUser] = useState(null);
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);


  // ---------------- Fetch logged-in user ----------------
  useEffect(() => {
    const fetchUser = async () => {
      try {
        const res = await fetch(`${BASE_URL}/get_user_details`, {
          method: "GET",
          credentials: "include",
        });
        if (!res.ok) throw new Error("Not logged in");
        const data = await res.json();
        setUser(data);
      } catch (err) {
        navigate("/checkout/login");
      }
      finally {
      setLoading(false);
        }
    };
    fetchUser();
  }, [navigate]);

  // ---------------- Fetch wishlist items ----------------
  const fetchWishlistItems = useCallback(async () => {
    if (!user) return;

    try {
      const res = await fetch(
        `${BASE_URL}/wishlist/fetch_wishlist_details?user_id=${user.user_id}`,
        {
          method: "GET",
          credentials: "include",
        }
      );

      if (!res.ok) {
        const errData = await res.json().catch(() => ({}));
        throw new Error(errData.error || "Failed to fetch wishlist");
      }

      const data = await res.json();

      // Normalize data: rename `id` → `wishlistId`
      const normalized = (data.items || []).map((item) => ({
        wishlistId: item.wishlist_item_id,
        item_id: item.item_id,
        ...item,
      }));

      setWishlistItems(normalized);
    } catch (err) {
      console.error("Error fetching wishlist:", err);
      setWishlistItems([]);
    }
  }, [user]);

  useEffect(() => {
    fetchWishlistItems();
  }, [fetchWishlistItems]);

  // ---------------- Remove item ----------------
  const handleRemoveItem = async (wishlistId) => {
    try {
      const res = await fetch(
        `${BASE_URL}/wishlist_items/delete_wishlist_item/${wishlistId}`,
        {
          method: "DELETE",
          credentials: "include",
        }
      );

      if (!res.ok) {
        const errData = await res.json();
        throw new Error(errData.error || "Failed to remove item");
      }

      setWishlistItems(perv => perv.filter(item => item.wishlistId !== wishlistId))

    } catch (err) {
      console.error("Error removing item:", err);
      alert("Failed to remove item from wishlist");
    }
  };

  // ---------------- Add to cart ----------------
  const handleAddToCart = async (product) => {
    try {
      if (!user) throw new Error("Please login to add to cart");

      // Get or create cart
      const resCart = await fetch(
        `${BASE_URL}/cart/fetch_cart_details?user_id=${user.user_id}`,
        {
          method: "GET",
          credentials: "include",
        }
      );

      let cart_id;
      if (resCart.status === 404) {
        const createRes = await fetch(`${BASE_URL}/cart/create_cart`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
          body: JSON.stringify({ user_id: user.user_id }),
        });
        const data = await createRes.json();
        cart_id = data.cart_id;
      } else {
        const data = await resCart.json();
        cart_id = data.cart_id;
      }

      const imageUrl = product.images?.length
      ? `${BASE_URL}/download_images/${
          typeof product.images[0] === "object" ? product.images[0].id : product.images[0]
        }`
      : "/assets/default.jpg";

      const payload = {
        cart_id,
        item_id: product.item_id,
        quantity: 1,
        name: product.name,
        price: Number(product.price),
        image: imageUrl
      };

      const resAdd = await fetch(`${BASE_URL}/cart_items/add_items_to_cart`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(payload),
      });

      if (!resAdd.ok) throw new Error("Failed to add item to cart");

      alert(`${product.name} added to cart!`);

      // Remove this item from wishlist after adding to cart
      await handleRemoveItem(product.wishlistId);
      
      window.dispatchEvent(new Event("cartUpdated"));
      navigate("/cart");
    } catch (err) {
      console.error("Add to cart error:", err);
      alert(err.message);
    }
  };

      // block UI until check completes
      if (loading) return null;

      // If no user, don't render wishlist at all
      if (!user) return null;


  return (
    <>
      <div className="wishlist-container">
        <h2>Your Wishlist</h2>
        <table className="wishlist-table">
          <thead>
            <tr>
              <th>Title</th>
              <th>Product</th>
              <th>Unit Price</th>
              <th>Stock Status</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {wishlistItems.length === 0 ? (
              <tr>
                <td colSpan="4" style={{ textAlign: "center" }}>
                  No items in wishlist
                </td>
              </tr>
            ) : (
              wishlistItems.map((item) => (
                <tr key={item.wishlistId}>
                  <td 
                      style={{ cursor: "pointer" }}
                      onClick={() => navigate(`/Product/${item.item_id}`)}>
                      {item.image_id ? (
                        <img
                            src={
                              item.image_id
                                ? `${BASE_URL}/wishlist/download_image/${item.image_id}`
                                : "/assets/default.jpg"
                            }
                            alt={item.name}
                            style={{ width: "60px", height: "60px", objectFit: "cover" }}
                          />
                      ) : (
                        "No image"
                      )}
                    </td>
                    <td 
                      style={{ cursor: "pointer" }}
                      onClick={() => navigate(`/Product/${item.item_id}`)}
                    >
                      {item.name}
                    </td>
                  <td>
                    ₹ {Number(item.price).toFixed(2)} (Excl. GST)
                     {/* ₹ {item.total_price.toFixed(2)} (Incl. GST) */}
                  </td>
                  <td>
                    {item.quantity > 0 ? (
                      <span className="stock-status in-stock">In Stock</span>
                    ) : (
                      <span className="stock-status out-of-stock">
                        Out of Stock
                      </span>
                    )}
                  </td>
                  <td>
                    {item.quantity > 0 && (
                      <button
                        className="wishlist-cart-btn"
                        onClick={() => handleAddToCart(item)}
                      >
                        Add to Cart
                      </button>
                    )}
                    <button
                      className="wishlist-remove-btn"
                      onClick={() => handleRemoveItem(item.wishlistId)}
                    >
                      Remove
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
      <Footer />
    </>
  );
};

export default Wishlist;

